import React, { useState } from "react";
import WhichBrowser from "which-browser";
import axios from "axios";

import {
    Button,
    ButtonProps,
    Grid,
    OutlinedInputProps,
    TextField,
    TextFieldProps,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    CircularProgress,
} from "@mui/material";

import { SelectChangeEvent } from "@mui/material/Select";

import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

import { useTypedDispatch, useTypedSelector } from "hooks/store";

import { openDialog } from "state/slices/DialogSlice";

const RedditTextField = styled((props: TextFieldProps) => (
    <TextField InputProps={{ disableUnderline: true } as Partial<OutlinedInputProps>} {...props} />
))(({ theme }) => ({
    "& .MuiFormControl-root": {
        marginTop: 0,
    },
    label: {
        color: "#02334E",
    },
    "& label.Mui-focused": {
        color: "#02334E",
    },
    "& .MuiInputBase-input": {
        color: "#02334E",
    },
    "& .MuiFilledInput-root": {
        border: "1px solid #e2e2e1",
        overflow: "hidden",
        borderRadius: 4,
        backgroundColor: theme.palette.mode === "light" ? "#fcfcfb" : "#2b2b2b",
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        "&:hover": {
            backgroundColor: "transparent",
        },
        "&.Mui-focused": {
            backgroundColor: "transparent",
            borderColor: "#E6117B",
        },
    },
}));

const ColorButton = styled(Button)<ButtonProps>(() => ({
    boxShadow: "none",
    textTransform: "none",
    fontSize: 14,
    fontWeight: "normal",
    border: "1px solid",
    color: "#fff",
    borderRadius: "50px",
    lineHeight: 1.5,
    backgroundColor: "#E6117B",
    borderColor: "#E6117B",
    padding: "10px",
    fontFamily: [
        "Open Sans",
        "-apple-system",
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
    ].join(","),
    "&:hover": {
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
        boxShadow: "none",
        color: "#fff",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
    },
    "&:disabled": {
        backgroundColor: "#D1D1D1",
        borderColor: "#D1D1D1",
        color: "#fff",
    },
}));

const DialogReportIssue = (): JSX.Element => {
    const API_URL =
        process.env.NODE_ENV === "development"
            ? "http://localhost:8787"
            : "https://zendesk.bridgesea.workers.dev";
    const [creatingTicket, setCreatingTicket] = useState<boolean>(false);

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<boolean>(false);

    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<boolean>(false);

    const [device, setDevice] = useState<string>("");
    const [deviceError, setDeviceError] = useState<boolean>(false);

    const [deviceAge, setDeviceAge] = useState<string>("");

    const [issue, setIssue] = useState<string>("");
    const [issueError, setIssueError] = useState<boolean>(false);

    const [issueSteps, setIssueSteps] = useState<string>("");
    const [issueStepsError, setIssueStepsError] = useState<boolean>(false);

    const [image, setImage] = useState<{ data: ArrayBuffer; name: string } | undefined>(undefined);
    const [imageAttached, setImageAttached] = useState<boolean>(false);

    const [confirm, setConfirm] = useState<boolean>(false);
    const validEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const dispatch = useTypedDispatch();
    const type = useTypedSelector((store) => store.Dialog.type);

    const handleDialogClose = (): void => {
        dispatch(openDialog({ isDialogOpen: false, type }));
    };

    const submitForm = async () => {
        let error = false;
        if (name.length <= 0) {
            setNameError(true);
            error = true;
        }

        if (!email.match(validEmail) || email.length <= 0) {
            setEmailError(true);
            error = true;
        }

        if (device === "") {
            setDeviceError(true);
            error = true;
        }

        if (issue.length <= 0) {
            setIssueError(true);
            error = true;
        }

        if (issueSteps.length <= 0) {
            setIssueStepsError(true);
            error = true;
        }

        if (error) {
            return;
        }

        const info = new WhichBrowser(window.navigator.userAgent);
        const data = {
            subject: `Support request from ${name}`,
            organisation_id: "6324627944477",
            name,
            email,
            device,
            deviceAge,
            issue,
            issueSteps,
            detected_browser: info.browser.name,
            detected_browser_version: info.browser.version.value,
            detected_device: info.device.model,
            detected_dive_type: info.device.type,
            detected_os: info.os.name,
            detected_os_version: info.os.version.value,
        };

        try {
            setCreatingTicket(true);
            let image_id: string | undefined = undefined;

            // Only try to upload the image if we have an attachment
            if (image !== undefined) {
                // Get image upload URL from Cloudflare worker
                const {
                    data: { message: ZENDESK_API },
                } = await axios.get(API_URL);

                // Set base URL for file API
                const fileApi = axios.create({
                    baseURL: ZENDESK_API,
                    headers: {
                        "Content-Type": "application/binary",
                    },
                });

                // Set Authorization header for file upload
                fileApi.defaults.headers.Authorization = `Basic ${btoa(`${email}:`)}`;

                const { data: uploadRes } = await fileApi.post("/", image.data, {
                    params: { filename: image.name },
                });

                image_id = uploadRes.upload?.token ?? undefined;
            }

            // Create ticket in ZenDesk
            await axios.post(API_URL, {
                ...data,
                // Only add the image to the ticket when we've uploaded one
                ...(image_id && { uploads: [image_id] }),
            });
        } catch (error) {
            console.error(error);
        } finally {
            setConfirm(true);
            setCreatingTicket(false);
        }

        return;
    };

    const closeConfirm = () => {
        handleDialogClose();

        setTimeout(() => {
            setConfirm(false);
        }, 500);
    };

    /**
     * Update state based on input type.
     * @param event
     */
    const handleUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentName: string = event.target.name;
        const value: string = event.target.value;
        switch (currentName) {
            case "name":
                if (value.length <= 0) {
                    setNameError(true);
                } else {
                    setNameError(false);
                }
                setName(value);
                break;

            case "email":
                if (value.length <= 0) {
                    setEmailError(true);
                } else {
                    setEmailError(false);
                }
                setEmail(value);
                break;

            case "device":
                if (value === "") {
                    setDeviceError(true);
                } else {
                    setDeviceError(false);
                }
                setDevice(value);
                break;

            case "deviceAge":
                setDeviceAge(value);
                break;

            case "issue":
                if (value.length <= 0) {
                    setIssueError(true);
                } else {
                    setIssueError(false);
                }
                setIssue(value);
                break;

            case "issueSteps":
                if (value.length <= 0) {
                    setIssueStepsError(true);
                } else {
                    setIssueStepsError(false);
                }
                setIssueSteps(value);
                break;
        }
    };

    const handleChange = (event: SelectChangeEvent) => {
        setDevice(event.target.value as string);
        if (event.target.value === "") {
            setDeviceError(true);
        } else {
            setDeviceError(false);
        }
    };

    // Read image to binary data
    const imageToBinary = async (file: Blob) => {
        return new Promise<ArrayBuffer>((resolve, reject) => {
            try {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as ArrayBuffer);
                reader.onerror = (error) => reject(error);
                reader.readAsArrayBuffer(file);
            } catch (error) {
                reject(error);
            }
        });
    };

    const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value != "") {
            setImageAttached(true);
        }
        if (event.target.files) {
            const file = event.target.files[0];
            const arrayBuffer = await imageToBinary(file);
            setImage({ data: arrayBuffer, name: file.name });
        } else {
            setImage(undefined);
        }
    };

    return (
        <>
            {!confirm ? (
                <section className="newSetupCard">
                    <header className="cardHeader">
                        <h2 style={{ marginBottom: "40px" }}>What issue are you experiencing?</h2>
                    </header>
                    <article className="cardBody">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    error={nameError}
                                    onChange={handleUpdate}
                                    helperText={nameError && "Name is required"}
                                    name="name"
                                    value={name}
                                    fullWidth
                                    label="Name*"
                                    id="name"
                                    variant="filled"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    error={emailError}
                                    onChange={handleUpdate}
                                    helperText={emailError && "Email is required"}
                                    name="email"
                                    value={email}
                                    fullWidth
                                    label="Email Address*"
                                    id="email"
                                    variant="filled"
                                />
                            </Grid>
                            <FormControl
                                error={deviceError}
                                sx={{ m: 1, minWidth: 200 }}
                                style={{
                                    marginTop: "30px",
                                    marginLeft: "25px",
                                    backgroundColor: "#fcfcfb",
                                }}
                            >
                                <InputLabel
                                    id="device-error-label"
                                    style={{ color: deviceError ? "#d32f2f" : "#02334E" }}
                                >
                                    Device*
                                </InputLabel>
                                <Select
                                    labelId="device-error-label"
                                    id="device-error"
                                    value={device}
                                    label="device"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="macbook">Macbook</MenuItem>
                                    <MenuItem value="ipad_pro/ipad">iPad Pro/ iPad</MenuItem>
                                    <MenuItem value="pc">PC</MenuItem>
                                    <MenuItem value="android">Android - Mobile</MenuItem>
                                    <MenuItem value="ios">iOS - Mobile</MenuItem>
                                </Select>
                                {deviceError ? (
                                    <FormHelperText>Device is required</FormHelperText>
                                ) : null}
                            </FormControl>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    onChange={handleUpdate}
                                    name="deviceAge"
                                    value={deviceAge}
                                    fullWidth
                                    label="What is the age of your device?"
                                    id="deviceAge"
                                    variant="filled"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    error={issueError}
                                    onChange={handleUpdate}
                                    helperText={issueError && "Issue is required"}
                                    name="issue"
                                    value={issue}
                                    fullWidth
                                    label="What is the issue that your experiencing?*"
                                    id="issue"
                                    variant="filled"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RedditTextField
                                    autoComplete="off"
                                    error={issueStepsError}
                                    onChange={handleUpdate}
                                    helperText={
                                        issueStepsError &&
                                        "Steps to replicate the issue is required"
                                    }
                                    name="issueSteps"
                                    value={issueSteps}
                                    fullWidth
                                    label="Steps to replicate the issue*"
                                    id="issueStepsError"
                                    variant="filled"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ position: "relative" }}>
                                    <div
                                        style={{
                                            backgroundColor: "#fcfcfb",
                                            border: "1px solid #e2e2e1",
                                            borderRadius: "4px",
                                            padding: "15px",
                                        }}
                                    >
                                        {imageAttached === true
                                            ? "Image is attached"
                                            : "Select image"}
                                    </div>
                                    <input
                                        type="file"
                                        onChange={onChange}
                                        accept="image/jpg, image/jpeg, image/png"
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            opacity: 0,
                                            cursor: "pointer",
                                        }}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={6} style={{ marginTop: "40px" }}>
                                <ColorButton
                                    onClick={() => handleDialogClose()}
                                    fullWidth={true}
                                    variant="contained"
                                    className="cancel"
                                    startIcon={<CloseIcon className="icon" />}
                                    style={{ backgroundColor: "#D1D1D1", borderColor: "#D1D1D1" }}
                                >
                                    Cancel
                                </ColorButton>
                            </Grid>
                            <Grid item xs={12} sm={6} style={{ marginTop: "40px" }}>
                                <ColorButton
                                    onClick={() => submitForm()}
                                    fullWidth={true}
                                    variant="contained"
                                    disabled={creatingTicket}
                                >
                                    {creatingTicket ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : (
                                        "Report"
                                    )}
                                </ColorButton>
                            </Grid>
                        </Grid>
                    </article>
                </section>
            ) : (
                <div className="confirmation">
                    <h2 style={{ textAlign: "center", marginBottom: "30px" }}>
                        Thank you. We will be in touch.
                    </h2>
                    <Grid item xs={12} style={{ marginTop: "40px" }}>
                        <ColorButton
                            onClick={() => closeConfirm()}
                            fullWidth={true}
                            variant="contained"
                            className="cancel"
                            startIcon={<CloseIcon className="icon" />}
                            style={{
                                backgroundColor: "#D1D1D1",
                                borderColor: "#D1D1D1",
                                width: "max-content",
                                margin: "auto",
                                display: "flex",
                            }}
                        >
                            Close
                        </ColorButton>
                    </Grid>
                </div>
            )}
        </>
    );
};
export default DialogReportIssue;
